import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// Make sure this action exists or adjust based on your slice name
import { fetchLamaForm } from "../../../store/slices/lamaFormsSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function LamaFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const lamaState = useSelector((state) => state.lamaForms || state.lama || {});
    const { currentLamaForm, isLoading, error } = lamaState;

    useEffect(() => {
        if (id) dispatch(fetchLamaForm(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentLamaForm) {
            const timer = setTimeout(() => window.print(), 800);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentLamaForm, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading LAMA Report...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentLamaForm) return <div className="p-8 text-center">No LAMA record found.</div>;

    const d = currentLamaForm;

    const fmtDate = (dateStr) =>
        dateStr ? new Date(dateStr).toLocaleDateString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        }) : "";

    const lamaReasons = [
        "Financial Constraints",
        "Family Concerns",
        "Dissatisfied with Patient Care",
        "Unsatisfactory Treatment",
        "Unsatisfactory Administrative Arrangements",
        "Other",
    ];

    return (
        <>
            <style jsx global>{`
        * { box-sizing: border-box !important; font-family: Arial, sans-serif !important; }
        body { background: #fff !important; color: #000 !important; margin: 0 !important; padding: 0 !important; }
        .print-container { padding: 30px !important; position: relative !important; }
        ${printFooterStyles}
        .logo { position: absolute !important; top: 20px !important; left: 20px !important; }
        .logo img { height: 70px !important; width: auto !important; }
        .header { text-align: center !important; margin-bottom: 30px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
        .header h3 { font-size: 1.6rem !important; font-weight: bold !important; margin-bottom: 5px !important; }
        .header h6 { font-size: 1.1rem !important; text-decoration: underline !important; }
        
        .section-title { background: #f2f2f2 !important; padding: 5px 10px !important; font-weight: bold !important; margin: 20px 0 10px 0 !important; border: 1px solid #000 !important; display: block !important; }
        .info-grid { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 15px !important; margin-bottom: 15px !important; }
        .info-item { font-size: 1rem !important; margin-bottom: 8px !important; }
        .underline { border-bottom: 1px solid #000 !important; font-weight: bold !important; padding: 0 5px !important; display: inline-block !important; min-width: 150px !important; }
        
        .checkbox-row { display: flex !important; align-items: center !important; margin-bottom: 10px !important; }
        .checkbox-box { width: 18px !important; height: 18px !important; border: 1px solid #000 !important; margin-right: 10px !important; text-align: center !important; line-height: 16px !important; font-weight: bold !important; }
        .ticked { background-color: #000 !important; color: #fff !important; }
        
        .text-box { border: 1px solid #000 !important; padding: 10px !important; min-height: 80px !important; margin-top: 5px !important; line-height: 1.5 !important; }
        .footer { margin-top: 60px !important; border-top: 1px solid #000 !important; padding-top: 10px !important; text-align: center !important; font-size: 0.9rem !important; }
        
        .sig-section { display: flex !important; justify-content: space-between !important; margin-top: 50px !important; }
        .sig-box { border-top: 1px solid #000 !important; width: 200px !important; text-align: center !important; padding-top: 5px !important; }
      `}</style>

            <PrintWrapper>
                <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>LEFT AGAINST MEDICAL ADVICE (LAMA) FORM</h6>
                </div>

                <span className="section-title">PATIENT & GUARDIAN DETAILS</span>
                <div className="info-grid">
                    <div className="info-item">Patient Name: <span className="underline">{d.patient_name}</span></div>
                    <div className="info-item">Guardian Name: <span className="underline">{d.guardian_name}</span></div>
                    <div className="info-item">Relation with Patient: <span className="underline">{d.relation_with_patient}</span></div>
                    <div className="info-item">CNIC of Guardian: <span className="underline">{d.guardian_cnic}</span></div>
                </div>

                <span className="section-title">REASON FOR LAMA (To be filled by Staff)</span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {lamaReasons.map((reason) => {
                        // Matching logic for the checkboxes
                        const isSelected = d.reason_for_lama === reason;
                        return (
                            <div key={reason} className="checkbox-row">
                                <span className={`checkbox-box ${isSelected ? "ticked" : ""}`}>{isSelected ? "✓" : ""}</span>
                                <span>{reason === "Other" ? `Other: ${d.other_reason || ""}` : reason}</span>
                            </div>
                        );
                    })}
                </div>

                <span className="section-title">CONSULTANT / SHIFT IN-CHARGE DETAILS</span>
                <div className="info-grid">
                    <div className="info-item">Name: <span className="underline">{d.consultant_name}</span></div>
                    <div className="info-item">Diagnosis: <span className="underline">{d.diagnosis}</span></div>
                    <div className="info-item">Date: <span className="underline">{fmtDate(d.date)}</span></div>
                    <div className="info-item">Time: <span className="underline">{d.time}</span></div>
                </div>

                <span className="section-title">PRP REMARKS</span>
                <div className="text-box">{d.prp_remarks || "No remarks provided."}</div>

                <div className="sig-section">
                    <div className="sig-box">Guardian Signature</div>
                    <div className="sig-box">Staff Signature</div>
                    <div className="sig-box">Consultant Signature</div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
            </PrintWrapper>
        </>
    );
}